import React from 'react';
import { Copy, FilePlus2, History, RefreshCcw, Search, Send, ShieldCheck } from 'lucide-react';
import {
  desktopRibbonInputClass,
  desktopRibbonSelectClass,
  RibbonDesktopButton,
} from '../../../components/ribbon/DesktopRibbonControls';
import { EnterpriseRibbon } from '../../../components/layout/EnterpriseRibbon';
import type { StatusFilter } from '../state/excelenciaFilters.store';

interface CategoryOption {
  value: string;
  label: string;
}

interface GovernancaRibbonProps {
  canManage: boolean;
  hasSelection: boolean;
  status: StatusFilter;
  category: string | 'all';
  availableCategories: CategoryOption[];
  searchValue: string;
  onCreate: () => void;
  onDuplicate: () => void;
  onPublish?: () => void;
  showPublish?: boolean;
  onOpenHistory: () => void;
  onOpenAudit: () => void;
  onRefresh: () => void;
  onStatusChange: (value: StatusFilter) => void;
  onCategoryChange: (value: string | 'all') => void;
  onSearchChange: (value: string) => void;
}

export function GovernancaRibbon({
  canManage,
  hasSelection,
  status,
  category,
  availableCategories,
  searchValue,
  onCreate,
  onDuplicate,
  onPublish,
  showPublish = false,
  onOpenHistory,
  onOpenAudit,
  onRefresh,
  onStatusChange,
  onCategoryChange,
  onSearchChange,
}: GovernancaRibbonProps) {
  return (
    <EnterpriseRibbon moduleId="workflow">
      <EnterpriseRibbon.Group>
        {/* Sempre visíveis */}
        <RibbonDesktopButton icon={<FilePlus2 strokeWidth={1.5} />} label="Novo" disabled={!canManage} onClick={onCreate} />
        <RibbonDesktopButton icon={<RefreshCcw strokeWidth={1.5} />} label="Atualizar" onClick={onRefresh} />

        {/* Contextuais — só aparecem quando há blueprint selecionado */}
        {hasSelection && (
          <>
            {showPublish && onPublish ? (
              <RibbonDesktopButton icon={<Send strokeWidth={1.5} />} label="Publicar" disabled={!canManage} onClick={onPublish} />
            ) : null}
            <RibbonDesktopButton icon={<Copy strokeWidth={1.5} />} label="Duplicar" disabled={!canManage} onClick={onDuplicate} />
            <RibbonDesktopButton icon={<History strokeWidth={1.5} />} label="Histórico" onClick={onOpenHistory} />
            <RibbonDesktopButton icon={<ShieldCheck strokeWidth={1.5} />} label="Auditoria" onClick={onOpenAudit} />
          </>
        )}
      </EnterpriseRibbon.Group>

      <EnterpriseRibbon.Filters>
        <select
          value={status}
          onChange={(event) => onStatusChange(event.target.value as StatusFilter)}
          className={desktopRibbonSelectClass}
          title="Status"
        >
          <option value="all">Todos</option>
          <option value="draft">Rascunho</option>
          <option value="published">Publicado</option>
          <option value="standard">Padrão</option>
          <option value="archived">Arquivado</option>
        </select>

        <select
          value={category}
          onChange={(event) => onCategoryChange(event.target.value)}
          className={desktopRibbonSelectClass}
          title="Tipo"
        >
          <option value="all">Todos os tipos</option>
          {availableCategories.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="flex w-[200px] shrink-0 items-center">
          <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
            <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
            <input
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Buscar"
              className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
            />
          </div>
        </div>
      </EnterpriseRibbon.Filters>
    </EnterpriseRibbon>
  );
}

export default GovernancaRibbon;
